/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import LabeledData from '../../../../forms/elements/LabeledData';
import { fullName } from '../../../module';
import Table from '../../../../layout/table/Table';
import { apostrophize } from '../../../../language/strings';
import { ColumnConfig } from '../../../../layout/table/columnConfig';
import { HistoricalMedia } from './historicalMedia';
import { sortByDateProp, sortByProp } from '../../../../language/module';
import { disableMediaAction, DisableMediaPayload } from './disableMediaAction';
import {
  getErroredDisableMediaHistory,
  getErroredMediaHistory,
  getGuestMediaHistory,
  getLoadingDisableMediaHistory,
  getLoadingMediaHistory
} from './module';
import { IAppState } from '../../../../core/redux/IAppState';
import { getMediaHistoryAction } from './getMediaHistoryAction';
import { css } from '@emotion/react';
import { formatDateAndTime } from '../../../../language/time';
import { COOL_GREY, WHITE } from '../../../../styles/colors';
import { StyledBoxedMessage } from '../../../../forms/elements/boxedMessage/StyledBoxedMessage';
import { BoxedErrorInverse } from '../../../../forms/elements/boxedMessage/boxedMessageStyles';
import { MediumText } from '../../../../styles/styles';
import { Row } from '../../../../layout/flexbox';
import { ROW_SPACING } from '../../../../forms/styles';
import { BigNeutral, BigSecondary } from '../../../../forms/elements/buttons';
import { useDispatch, useSelector} from 'react-redux';
import { Loader } from '../../../../forms/elements/loader/Loader';
import {MediaStatusSelector} from "./MediaStatusSelector";
import { getResource } from '../../../../language/resources/getResource';
import { showSnackbarAction } from '../../../../snackbar/snackbarAction';

const mediaHistoryTableContainer = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const mediaHistoryTableStyle = css`
  max-height: 500px;  
  overflow-y: auto;   
  scrollbar-gutter: stable;  
  padding-bottom: 85px; 
  padding-right: 5px;
`;

const disabledMediaRowStyle = css`
  & > * {
    font-style: italic;
    background-color: ${COOL_GREY};
    color: ${WHITE};
  }
`;

type Props = {
  guestId: string;
  allowDisableMedia: boolean;
}


export const MediaHistoryModalTable = (props: Props) => {
  const dispatch = useDispatch();
  const userId = useSelector((state: IAppState) => state.userState.userId);
  const resortAbbrev = useSelector((state: IAppState) => state.selectedResort.resortAbbreviation);
  const guest = useSelector((state: IAppState) => state.guests.get(props.guestId));
  const mediaHistory = useSelector((state: IAppState) => getGuestMediaHistory(state, props.guestId));
  const isLoadingMediaHistory = useSelector((state: IAppState) => !!getLoadingMediaHistory(state, props.guestId));
  const isLoadingDisableHistory = useSelector((state: IAppState) => !!getLoadingDisableMediaHistory(state, props.guestId));
  const mediaHistoryLoadError = useSelector((state: IAppState) => !!getErroredMediaHistory(state, props.guestId));
  const disableMediaHistoryLoadError = useSelector((state: IAppState) => !!getErroredDisableMediaHistory(state, props.guestId));
  const operatorId = useSelector((state: IAppState) => state.userState.userName);
  const canDisableMedia = useSelector((state: IAppState) => state.resortToolsConfig.enableDisableMedia.includes(state.userState.role || ''));
  const displayDeactivatedMediaHistory = useSelector((state: IAppState) => state.resortToolsConfig.displayDeactivatedMediaHistory);
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const timeFormat = useSelector((state: IAppState) => state.defaultTimeFormat);

  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [disableMediaPayload, setDisableMediaPayload] = useState<DisableMediaPayload | undefined>();

  const columnConfig: Array<ColumnConfig<HistoricalMedia>> = [
    { id: 1, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_ID'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_ID'), onSort: sortByProp('mediaID', true) as any },
    { id: 2, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_RFID'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_RFID'), onSort: sortByProp('chipID', true) as any },
    { id: 3, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_AGENT'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_AGENT'), onSort: sortByProp('operatorID', true) as any},
    { id: 4, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_PRINT_DATE'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_PRINT_DATE'), onSort: sortByDateProp('printDate') as any},
    { id: 5, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_STATUS'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_PRINT_STATUS'), onSort: sortByProp('printStatus', true) as any },
    { id: 6, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_RESORT'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_RESORT'), onSort: sortByProp('resortAbbrv', true) as any},
    { id: 7, tooltipText: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_STATUS'), title: getResource(resources, 'MEDIA_HISTORY_MODAL_COLUMN_STATUS'), onSort: sortByProp('statusCode', true) as any, defaultSortingColumn: true,
      defaultSortingDirection: 'asc'},
  ];

  const wasLoadingDisableHistory = useRef(false);

  useEffect(() => {
    dispatch(getMediaHistoryAction(props.guestId));
  }, [dispatch, props.guestId]);

  useEffect(() => {
    if (wasLoadingDisableHistory.current && !isLoadingDisableHistory) {
      // Only fires when loading genuinely completes, not on mount
      if (!disableMediaHistoryLoadError && disableMediaPayload) {
        dispatch(showSnackbarAction(getResource(resources, 'MEDIA_HISTORY_MODAL_DISABLE_MEDIA_SUCCESS_MESSAGE', disableMediaPayload.mediaId)));
      }
      dispatch(getMediaHistoryAction(props.guestId));
      setDisableMediaPayload(undefined);
    }
    wasLoadingDisableHistory.current = isLoadingDisableHistory;
  }, [isLoadingDisableHistory, disableMediaHistoryLoadError, disableMediaPayload, dispatch, resources, props.guestId]);


  const disableMedia = () => {
    if (disableMediaPayload) {
      disableMediaPayload.userId = userId;
      disableMediaPayload.resortAbbreviation = resortAbbrev;
      disableMediaPayload.operatorId = operatorId;
      dispatch(disableMediaAction(disableMediaPayload, props.guestId));
      // NOTE: Unsure why this extra call to getMediaHistoryAction was here so leaving commented out for now
      dispatch(getMediaHistoryAction(props.guestId));
      setShowConfirmationMessage(false)
    }
  };

  const handleDisableMedia = (disableMediaPayload: DisableMediaPayload) => {
    setDisableMediaPayload(disableMediaPayload);
    setShowConfirmationMessage(true);
  }
  const hideConfirmationMessage = useCallback(
    () => {
      setShowConfirmationMessage(false);
      setDisableMediaPayload(undefined)
    }, []);

  const renderRow = (historicalMedia: HistoricalMedia): React.ReactNode => (
        <tr
            key={historicalMedia.mediaID}
            css={(historicalMedia.dtlDisabled || (displayDeactivatedMediaHistory && historicalMedia.statusCode !== 1)) ? disabledMediaRowStyle : ''}
        >
          <td>{historicalMedia.mediaID}</td>
          <td>{historicalMedia.chipID}</td>
          <td>{historicalMedia.operatorID}</td>
          <td css={css`max-width: 120px;`}>{
            historicalMedia.printStatus === 'Not Printed'
                ? ''
                : formatDateAndTime(historicalMedia.printDate, dateFormat, timeFormat)
          }</td>
          <td>
            {historicalMedia.printStatus}
          </td>
          <td>{historicalMedia.resortAbbrv}</td>
          <td css={css`width: 135px;text-align: center;`}>
            <MediaStatusSelector 
              canDisableMedia={canDisableMedia} 
              displayDeactivatedMediaHistory={displayDeactivatedMediaHistory}  
              disableMediaPayload={disableMediaPayload} 
              hideConfirmationMessage={hideConfirmationMessage} 
              disableMedia={handleDisableMedia}  
              historicalMedia={historicalMedia} 
              operatorId = {operatorId}
            />
          </td>
        </tr>
    );

  const renderConfirmationMessage = () => (
    <div>
      <StyledBoxedMessage
        boxStyle={BoxedErrorInverse}
        message={getResource(resources, 'MEDIA_HISTORY_MODAL_MESSAGE')}
      />

      <span
        css={css`
          display: block;
          ${MediumText};
          padding-top: 25px;`}
      >
          {getResource(resources, 'MEDIA_HISTORY_MODAL_CONFIRMATION', disableMediaPayload?.mediaId)}
        </span>

      <Row
        justifyContent='flex-end' rootCss={css`
          padding-top: 25px;
          padding-bottom: 1px;
          ${ROW_SPACING};`}
      >
        <BigSecondary onClick={hideConfirmationMessage}>{getResource(resources, 'MEDIA_HISTORY_MODAL_CANCEL')}</BigSecondary>
        <BigNeutral onClick={disableMedia}>{getResource(resources, 'MEDIA_HISTORY_MODAL_CONFIRM')}</BigNeutral>
      </Row>
    </div>
  );

  const isLoading = isLoadingDisableHistory || isLoadingMediaHistory;

  return (
    <>
      {/* Guest Name */}
      <LabeledData label={getResource(resources, 'RFID_FORM_LABEL')} data={fullName(guest)} />
      {
        isLoading
          ? <Loader />
          : <div css={mediaHistoryTableContainer} >
              <Table
                rootCss={mediaHistoryTableStyle}
                columns={columnConfig}
                data={mediaHistory!}
                isLoading={isLoadingMediaHistory!}
                noDataMessage={getResource(resources, 'MEDIA_HISTORY_MODAL_NO_DATA')}
                error={
                  mediaHistoryLoadError
                    ? getResource(resources, 'MEDIA_HISTORY_MODAL_FAILED_FETCH', apostrophize(fullName(guest)))
                    : undefined
                }
                renderRow={renderRow}
              />
            </div>
      }

      {showConfirmationMessage && renderConfirmationMessage()}
    </>
  );
};

